import {_} from 'lodash';
import {parseInt} from "lodash";
import * as ko from "knockout";
import {AbstractHistory, template as tpl} from "./AbstractHistory";
import {IHistory, IImportHrFile, IKnockoutComponent, IRouteDefinitionParams, ITableColumn} from "../../../index";
import {global} from "../../../appContext";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {getObjectType} from "../../../util/misc";
import {ObjectTypeId} from "../../../constants";
import {IAllCheckbox} from "../../search/th-checkbox";
import {nsSearchToggle} from "../../search/nsSearchToggle";
import {ISortableTableParams} from "../../sortable/index";
import {IParams as IParams_RecreateHistory} from "../dialog/recreate.history";
import {IDialogOptionsComponent} from "../../dialog/index";
import {nsFa} from "../../fa/fn";
import PromiseRequest from "../../../req/PromiseRequest";
import {isRoutePermissionOkay} from "../../../constants/RouteDefinitions";
import i = nsFa.i;
import ItemCheckbox = nsSearchToggle.ItemCheckbox;
import import_nav_bar_leftHtml from './import-nav-bar-left.html?raw';

const tplNavBarLeft = import_nav_bar_leftHtml
export const template = tpl.replace('<!-- TEMPLATE_NAV_BAR_LEFT -->', tplNavBarLeft);

export class viewModel extends AbstractHistory {

    protected readonly heading = 'view.import.history'
    protected selectedObjects: KnockoutObservableArray<IHistory> = ko.observableArray();
    protected allCheckbox: IAllCheckbox<IHistory>
    protected override _original = undefined as unknown as IImportHrFile;
    private readonly req_getimporthrfile = new PromiseRequest('getimporthrfile')

    protected TABLE: KnockoutComputed<ITableColumn<IHistory>[]> = ko.pureComputed((): ITableColumn<IHistory>[] => {
        const ret: ITableColumn<IHistory>[] = [];
        ret.push({
            key: "id",
            sort: true,
        }, {
            key: "registered",
            sort: true,
        }, {
            key: "displayname",
            sort: true,
        }, {
            key: "entrytype",
            sort: true,
        }, {
            key: "attributename",
            sort: true,
        }, {
            key: "oldvalue",
            sort: true,
            css: 'wordBreak--all',
        }, {
            key: "newvalue",
            sort: true,
            css: 'wordBreak--all',
        }, {
            key: 'departmentid',
            sort: true,
            label: 'department',
            showContent: o => !!parseInt(o.departmentid ?? '0'),
        });

        if (this.toggleComment()) {
            ret.push({
                key: "comment",
                sort: true,
            });
        }

        ret.push({
            label: "error message",
            key: "lasterror",
            sort: true,
            css: 'wordBreak--all text-danger',
        }, {
            label: "synchronization step",
            key: "syncstepid",
            getComponent: o => {
                return {
                    name: 'component/variable/syncstep-or-companysyncstep',
                    params: {id: o.syncstepid, linkable: isRoutePermissionOkay('syncstatusv2')},
                }
            },
            sort: true,
        });

        if (global.permissionValidator.isMinSuperManager()) {
            ret.unshift({
                key: "selector" as ITableColumn<IHistory>['key'],
                showContent: (o: IHistory) => !!o.lasterror,
            })
        }

        return ret;
    });

    constructor(params: IRouteDefinitionParams) {
        super(params);

        const pageObjectsWithErrors = ko.pureComputed(() => {
            return this.dataPage().filter(o => !!o.lasterror);
        })

        const selectedIds = ko.pureComputed(() => {
            return this.selectedObjects().map(o => o.id);
        })

        const pageObjectsNotSelected = ko.pureComputed(() => {
            return pageObjectsWithErrors().filter(o => !selectedIds().includes(o.id))
        })

        const pageObjectsSelected = ko.pureComputed(() => {
            return pageObjectsWithErrors().filter(o => selectedIds().includes(o.id))
        })

        const toggle = () => {
            if (pageObjectsNotSelected().length) {
                this.selectedObjects.push(...pageObjectsNotSelected())
            } else {
                pageObjectsWithErrors().forEach(o => this.selectedObjects.remove(o))
            }
        }

        const isChecked = ko.pureComputed((): boolean => {
            return !pageObjectsNotSelected().length && !!pageObjectsWithErrors().length
        })

        const isIndeterminate = ko.pureComputed((): boolean => {
            return !!pageObjectsSelected().length && !!pageObjectsNotSelected().length
        })

        const isEnabled = ko.pureComputed((): boolean => {
            return !!pageObjectsWithErrors().length
        })

        this.allCheckbox = {
            toggle: toggle,
            isChecked: isChecked,
            isIndeterminate: isIndeterminate,
            isEnabled: isEnabled,
            selected: this.selectedObjects,
        }

        this.init();
    }

    protected override init(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        return this.req_getimporthrfile
            .request({
                importfileid: global.router.currentRoute()?.__routeParams?.['importfileid'],
            })
            .then((data: [IImportHrFile]) => {
                this._original = data[0];
            })
            .then(() => super.init());
    }

    private numAffectedObjectsText(num: number = this._getObjectsWithErrors().length) {
        const objectTypes = this._getUniqueObjectTypes().map((o: ObjectTypeId) => getObjectType(o))
        const objectName: string = objectTypes.length == 1
            ? (objectTypes[0]?.name ?? 'object')
            : 'object'
        return `${num} ((${objectName}|n:${num}|ac))`
    }

    private _getNumUniqueHistoryTargets(): number {
        return _(this._getObjectsWithErrors())
            .uniqBy((o: IHistory) => o.userid)
            .value()
            .length
    }

    private _getObjectsWithErrors(): IHistory[] {
        let arr: IHistory[]
        if (this.selectedObjects().length) {
            arr = this.selectedObjects()
        } else {
            arr = this._original.objecthistories
        }
        return _(arr)
            .filter((o: IHistory) => !!o.lasterror)
            .uniqBy((o: IHistory) => `${o.userid},${o.syncstepid}`)
            .value()
    }

    private _getUniqueObjectTypes(): ObjectTypeId[] {
        return _(this._getObjectsWithErrors())
            .map((o: IHistory) => o.objecttypeid)
            .uniq()
            .value()
    }

    protected override getTableComponent(): IKnockoutComponent {
        const params: Partial<ISortableTableParams<IHistory>> = {
            allCheckbox: this.allCheckbox,
        }

        const getNumObjectsLabel = this.numAffectedObjectsText.bind(this)

        const getTitle = (num: number) => {
            return `${i('fa-recycle')} ((restore)) [${getNumObjectsLabel(num)}]`
        }

        if (global.permissionValidator.isMinSuperManager()) {
            params.getNavComponent = (): IKnockoutComponent => ({
                name: 'component/object/history/import-nav-bar-left',
                params: {
                    getNumObjectsLabel: this.numAffectedObjectsText.bind(this),
                    data: this._getObjectsWithErrors.bind(this),
                    onRecreateObjectsClick: () => {
                        const params: Partial<IParams_RecreateHistory> = {
                            data: this._getObjectsWithErrors.bind(this) as any,
                            getNumObjectsLabel: getNumObjectsLabel,
                            getTitle: getTitle.bind(this)
                        };

                        const dialogOptionsComponent: IDialogOptionsComponent = {
                            content: {
                                name: 'component/object/dialog/recreate.history',
                                params: params,
                            },
                            title: ko.observable(getTitle(this._getObjectsWithErrors().length)),
                        }

                        global.dialogManager.addComponent(dialogOptionsComponent)
                    }
                },
            })
        }
        return _.defaultsDeep({params: params}, super.getTableComponent())
    }

    protected _getObjectHistory(): IHistory[] {
        return this._original?.objecthistories ?? [];
    }

    protected getItemCheckbox(item: IHistory): ItemCheckbox<IHistory> {
        return new ItemCheckbox({
            item: item,
            key: 'id',
            selected: this.selectedObjects,
        })
    }

    override dispose() {
        super.dispose();
        this.req_getimporthrfile.abort()
    }
}
